/*
  DORVETTI — Header y mega menú: controlador (MOV-012). Sin librerías.

  - Estados por scroll (transparente sobre el hero, sólido, compacto, oculto al bajar si se activa):
    un único listener pasivo agrupado en rAF; solo se tocan clases cuando cambian.
  - Compacto (MOV-014): al salir de arriba, barra sólida con wordmark pequeño; el header grande
    vuelve solo al llegar arriba del todo (histéresis: entra a 40 px, sale a 2 px).
  - En escritorio este Horizon desplaza `.page-wrapper`, no la ventana: se detecta.
  - Mega menú: intención de hover con ratón, clic/tap, teclado (Escape, salida del foco).
  - Móvil: cajón con acordeón, usando el bloqueo de scroll de Horizon (html[scroll-lock]).
  - Búsqueda y bolsa abren el modal y el cajón nativos de Horizon (atributos on:click).
*/

(() => {
  'use strict';

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const headers = new Map();
  const COMPACT_AT = 40;
  const EXPAND_AT = 2;

  function getScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const overflowY = getComputedStyle(wrapper).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && wrapper.scrollHeight > wrapper.clientHeight + 1) {
        return wrapper;
      }
    }
    return window;
  }

  const scrollTopOf = (scroller) => (scroller === window ? window.scrollY : scroller.scrollTop);

  class DvHeader {
    constructor(root) {
      this.root = root;
      this.section = root.parentElement;
      this.topRow = root.querySelector('[data-dv-top]');
      this.announce = document.querySelector('[data-dv-announce]');
      this.bar = root.querySelector('[data-dv-bar]');
      this.burger = root.querySelector('[data-dv-burger]');
      this.scrim = this.section.querySelector('[data-dv-scrim]');
      this.items = Array.from(root.querySelectorAll('[data-dv-item]'));
      this.overlay = root.dataset.overlay === 'true';
      this.hideOnScroll = root.dataset.hideOnScroll === 'true';
      // MOV-030: sin hero de vídeo, la barra compacta desde el principio.
      this.compactAlways = root.dataset.compactAlways === 'true';
      this.openItem = null;
      this.drawerOpen = false;
      this.hover = false;
      this.hidden = false;
      this.compact = false;
      this.lastY = 0;
      this.heroEnd = 0;
      this.topH = 0;
      this.barH = 0;
      this.pending = false;
      this.openTimer = 0;
      this.closeTimer = 0;
      this.scroller = getScroller();

      this.onScroll = (event) => {
        const target = event.target;
        if (target === document || target === this.scroller) this.request();
      };
      this.onResize = () => {
        this.scroller = getScroller();
        this.measure();
        this.request();
      };
      this.onBreakpoint = () => {
        this.hover = false;
        this.closePanel();
        this.setDrawer(false);
        this.onResize();
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        // Con el cajón móvil abierto, Escape lo cierra entero (acordeones incluidos).
        if (this.drawerOpen) {
          this.setDrawer(false);
          this.burger?.focus();
        } else if (this.openItem) {
          const trigger = this.openItem.querySelector('[data-dv-trigger]');
          this.closePanel();
          trigger?.focus();
        }
      };
      this.onFocusOut = (event) => {
        if (!desktopQuery.matches || !this.openItem) return;
        if (!this.root.contains(event.relatedTarget)) this.closePanel();
      };
      // Como en la referencia: con el ratón encima, el header entero pasa al fondo claro aunque no se abra
      // ningún panel; al salir vuelve al degradado.
      this.onPointerEnterRoot = (event) => {
        window.clearTimeout(this.closeTimer);
        if (event.pointerType !== 'mouse' || !desktopQuery.matches || this.hover) return;
        this.hover = true;
        this.applyState();
      };
      this.onPointerLeaveRoot = (event) => {
        if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
        window.clearTimeout(this.openTimer);
        this.closeTimer = window.setTimeout(() => {
          this.hover = false;
          this.closePanel();
          this.applyState();
        }, 180);
      };
      // MOV-019: si el ratón sube desde el menú hacia el wordmark (fila superior), el mega menú se
      // cierra; si baja hacia el panel, sigue abierto.
      this.onTopEnter = (event) => {
        if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
        window.clearTimeout(this.openTimer);
        if (this.openItem) this.closePanel();
      };
      this.onScrimClick = () => this.closePanel();
      this.onBurgerClick = () => this.setDrawer(!this.drawerOpen);

      this.bindItems();
      // En document: Safari no enfoca los botones al pulsarlos y el foco puede quedar fuera.
      document.addEventListener('keydown', this.onKeydown);
      this.root.addEventListener('focusout', this.onFocusOut);
      this.root.addEventListener('pointerenter', this.onPointerEnterRoot);
      this.root.addEventListener('pointerleave', this.onPointerLeaveRoot);
      this.topRow?.addEventListener('pointerenter', this.onTopEnter);
      this.scrim?.addEventListener('click', this.onScrimClick);
      this.burger?.addEventListener('click', this.onBurgerClick);
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', this.onResize, { once: true });
      desktopQuery.addEventListener?.('change', this.onBreakpoint);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.root);
      }

      this.watchCart();
      this.measure();
      this.update(true);
    }

    destroy() {
      document.removeEventListener('keydown', this.onKeydown);
      this.root.removeEventListener('focusout', this.onFocusOut);
      this.root.removeEventListener('pointerenter', this.onPointerEnterRoot);
      this.root.removeEventListener('pointerleave', this.onPointerLeaveRoot);
      this.topRow?.removeEventListener('pointerenter', this.onTopEnter);
      this.scrim?.removeEventListener('click', this.onScrimClick);
      this.burger?.removeEventListener('click', this.onBurgerClick);
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onResize);
      desktopQuery.removeEventListener?.('change', this.onBreakpoint);
      this.resizeObserver?.disconnect();
      this.itemCleanups?.forEach((cleanup) => cleanup());
      this.setDrawer(false);
      this.closePanel();
    }

    bindItems() {
      this.itemCleanups = this.items.map((item) => {
        const trigger = item.querySelector('[data-dv-trigger]');
        const onEnter = (event) => {
          if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
          window.clearTimeout(this.closeTimer);
          window.clearTimeout(this.openTimer);
          if (!trigger) {
            this.closeTimer = window.setTimeout(() => this.closePanel(), 120);
            return;
          }
          const delay = this.openItem ? 0 : 90;
          this.openTimer = window.setTimeout(() => this.openPanel(item), delay);
        };
        const onClick = (event) => {
          window.clearTimeout(this.openTimer);
          // Con ratón en escritorio el hover ya abre el panel: el clic nunca lo cierra de golpe.
          if (event.pointerType === 'mouse' && desktopQuery.matches) {
            this.openPanel(item);
            return;
          }
          if (this.openItem === item) this.closePanel();
          else this.openPanel(item);
        };
        item.addEventListener('pointerenter', onEnter);
        trigger?.addEventListener('click', onClick);
        return () => {
          item.removeEventListener('pointerenter', onEnter);
          trigger?.removeEventListener('click', onClick);
        };
      });
    }

    openPanel(item) {
      if (this.openItem === item) return;
      this.closePanel(true);
      const trigger = item.querySelector('[data-dv-trigger]');
      if (!trigger) return;
      this.openItem = item;
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.root.classList.add('is-open');
      if (desktopQuery.matches) this.scrim?.classList.add('is-visible');
      this.applyState();
    }

    closePanel(keepScrim = false) {
      if (!this.openItem) return;
      const item = this.openItem;
      this.openItem = null;
      item.classList.remove('is-open');
      item.querySelector('[data-dv-trigger]')?.setAttribute('aria-expanded', 'false');
      this.root.classList.remove('is-open');
      if (!keepScrim) this.scrim?.classList.remove('is-visible');
      this.applyState();
    }

    setDrawer(open) {
      if (this.drawerOpen === open) return;
      this.drawerOpen = open;
      this.root.classList.toggle('is-drawer-open', open);
      this.burger?.setAttribute('aria-expanded', String(open));
      if (open) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.documentElement.removeAttribute('scroll-lock');
        this.closePanel();
      }
      this.applyState();
    }

    measure() {
      const scrollTop = scrollTopOf(this.scroller);
      const announceH = this.announce && !this.announce.hidden ? this.announce.offsetHeight : 0;
      this.topH = this.topRow ? this.topRow.offsetHeight : 0;
      document.documentElement.style.setProperty('--dv-announce-h', `${announceH}px`);
      this.barH = this.bar ? this.bar.offsetHeight : 0;
      const total = this.root.offsetHeight;
      this.root.style.setProperty('--dv-top-h', `${this.topH}px`);
      document.documentElement.style.setProperty('--dv-header-h', `${total + announceH}px`);
      document.documentElement.style.setProperty('--dv-bar-h', `${this.barH}px`);
      // Consumidores nativos de Horizon (elementos sticky, alturas de sección).
      document.body.style.setProperty('--header-height', `${this.barH}px`);
      document.body.style.setProperty('--header-group-height', `${this.barH}px`);

      const scrollbar = this.scroller === window ? 0 : this.scroller.offsetWidth - this.scroller.clientWidth;
      this.root.style.setProperty('--dv-scrollbar', `${Math.max(0, scrollbar)}px`);
      document.documentElement.style.setProperty('--dv-scrollbar', `${Math.max(0, scrollbar)}px`);

      if (this.overlay) {
        const first = document.querySelector('#MainContent > .shopify-section');
        this.heroEnd = first ? first.getBoundingClientRect().bottom + scrollTop : 0;
      }
    }

    request() {
      if (this.pending) return;
      this.pending = true;
      window.requestAnimationFrame(() => this.update());
    }

    update(initial = false) {
      this.pending = false;
      const y = Math.max(0, scrollTopOf(this.scroller));
      const delta = y - this.lastY;
      const desktop = desktopQuery.matches;
      const threshold = desktop ? this.topH : this.barH;

      if (y <= 2) {
        this.hidden = false;
      } else if (this.hideOnScroll && !this.openItem && !this.drawerOpen) {
        if (delta > 3 && y > threshold + this.barH) this.hidden = true;
        else if (delta < -3) this.hidden = false;
      }
      if (initial) this.hidden = false;

      if (this.compactAlways) this.compact = true;
      else if (y <= EXPAND_AT) this.compact = false;
      else if (y > COMPACT_AT) this.compact = true;
      this.solid = !this.overlay || y > this.heroEnd - this.barH;
      this.lastY = y;
      this.applyState();
    }

    applyState() {
      // Sobre el hero con el header grande: degradado en reposo y fondo (crema, en el CSS) con el
      // ratón encima o con un panel abierto.
      const overHero = this.overlay && !this.solid && !this.compact && !this.drawerOpen;
      const transparent = overHero && !this.openItem && !this.hover;
      const warm = overHero && !this.openItem && this.hover;
      const hidden = this.hidden && !this.openItem && !this.drawerOpen;
      // Al cambiar entre header grande y barra compacta, las entradas del menú se deslizan a su nueva
      // posición en vez de saltar (técnica FLIP). En pantallas anchas no se mueven y no hay animación.
      const compactChanges = this.root.classList.contains('is-compact') !== this.compact;
      const animateNav = compactChanges && desktopQuery.matches && !reducedMotion.matches;
      const navItems = animateNav ? Array.from(this.root.querySelectorAll('.dv-nav__list > li')) : [];
      const before = navItems.map((el) => el.getBoundingClientRect().left);
      this.toggle('is-transparent', transparent);
      this.toggle('is-hover', warm);
      this.toggle('is-solid', !transparent);
      this.toggle('is-compact', this.compact);
      this.toggle('is-hidden', hidden);
      navItems.forEach((el, i) => {
        const dx = before[i] - el.getBoundingClientRect().left;
        if (Math.abs(dx) > 1 && el.animate) {
          el.animate([{ transform: `translate3d(${dx}px, 0, 0)` }, { transform: 'translate3d(0, 0, 0)' }], {
            duration: 700,
            easing: 'cubic-bezier(0.45, 0, 0.15, 1)'
          });
        }
      });
      if (hidden && this.openItem) this.closePanel();
    }

    toggle(name, value) {
      if (this.root.classList.contains(name) !== value) this.root.classList.toggle(name, value);
    }

    watchCart() {
      const badge = this.root.querySelector('[data-dv-cart-count]');
      if (!badge) return;
      const render = (count) => {
        badge.textContent = String(count);
        badge.hidden = !count;
      };
      import('@shopify/events')
        .then(({ StandardEvents }) => {
          if (!StandardEvents?.cartLinesUpdate) return;
          document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
            event.promise
              ?.then(({ cart, detail }) => render(cart?.totalQuantity ?? detail?.itemCount ?? 0))
              .catch(() => {});
          });
        })
        .catch(() => {});
    }

    revealBlock(target) {
      const item = target.closest?.('[data-dv-item]');
      if (!item || !this.root.contains(item)) return;
      if (!desktopQuery.matches) this.setDrawer(true);
      if (item.querySelector('[data-dv-trigger]')) this.openPanel(item);
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-dv-header]').forEach((root) => {
      if (!headers.has(root)) headers.set(root, new DvHeader(root));
    });
  }

  function destroyWithin(scope) {
    headers.forEach((header, root) => {
      if (scope.contains(root)) {
        header.destroy();
        headers.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  // Editor de temas: al seleccionar un bloque del menú, se abre su panel.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    headers.forEach((header) => header.revealBlock(event.target));
  });
  document.addEventListener('shopify:block:deselect', () => {
    headers.forEach((header) => header.closePanel());
  });
})();
